import { useMemo, memo } from 'react';
import styled from 'styled-components';

type Props = {
  /** Input string to search for */
  searchText?: string;
  /** True if the search is case sensitive */
  isCaseSensitive?: boolean;
  /** Text displayed by this component and what the search is being done on */
  text?: string;
};

type TextSection = {
  text: string;
  match: boolean;
  tag: string;
};

const HighlightedMatch = styled.span`
  background: #fffec5;
`;

const WrappedText = styled.span`
  word-break: break-word;
`;

/** Ensures that special characters do not break search */
export const escapeSpecialCharacters = (text: string) =>
  text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const sectionText = (searchText: string, text: string, isCaseSensitive = false): TextSection[] => {
  if (searchText === '' || text === '') {
    return [{ text, match: false, tag: '0-unmatched' }];
  }
  const searchRegex = new RegExp(escapeSpecialCharacters(searchText), isCaseSensitive ? 'g' : 'gi');
  const unmatched = text.split(searchRegex);
  const matched = Array.from(text.matchAll(searchRegex), (match) => match[0]);
  const combinedSections = matched.reduce(
    (sections, match, index): TextSection[] => [
      ...sections,
      { text: unmatched[index], match: false, tag: `${index}-unmatched` },
      { text: match, match: true, tag: `${index}-matched` },
    ],
    [] as TextSection[],
  );

  combinedSections.push({
    text: unmatched[unmatched.length - 1],
    match: false,
    tag: `${unmatched.length - 1}-unmatched`,
  });

  return combinedSections;
};

export const SearchableText = ({ searchText = '', isCaseSensitive = false, text = '' }: Props) => {
  const sectionedText = useMemo(
    () => sectionText(searchText, text, isCaseSensitive),
    [searchText, text],
  );
  return (
    <>
      {sectionedText.map((section) =>
        section.match ? (
          <HighlightedMatch key={section.tag} data-test={section.tag}>
            {section.text}
          </HighlightedMatch>
        ) : (
          <WrappedText key={section.tag} data-test={section.tag}>
            {section.text}
          </WrappedText>
        ),
      )}
    </>
  );
};

export const MemoSearchableText = memo(SearchableText);
